import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../../shared/hooks/useApi'
import { formatDateTime, formatFileSize, getNoticeTypeLabel } from '../../shared/utils/formatters'
import { BarChart3, Database, Calendar, HardDrive } from 'lucide-react'

interface StatsData {
  total_notices?: number
  database_size_mb?: number
  notices_by_type?: Record<string, number>
  last_sync?: string
}

interface StatCardProps {
  title: string
  value: string
  icon: React.ComponentType<{ className?: string }>
  color?: 'blue' | 'green' | 'purple' | 'orange'
  'data-testid'?: string
}

function StatCard({ title, value, icon: Icon, color = 'blue', 'data-testid': testId }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-brand-bg text-brand',
    green: 'bg-success/10 text-success',
    purple: 'bg-ai/10 text-ai',
    orange: 'bg-warning/10 text-warning',
  }

  return (
    <div className="bg-surface-raised rounded-lg border border-border-subtle p-6" data-testid={testId}>
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-ink-secondary">{title}</p>
          <p className="text-2xl font-semibold text-ink">{value}</p>
        </div>
      </div>
    </div>
  )
}

interface NoticeTypeChartProps {
  noticesByType?: Record<string, number>
}

function NoticeTypeChart({ noticesByType }: NoticeTypeChartProps) {
  if (!noticesByType || Object.keys(noticesByType).length === 0) {
    return (
      <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
        <h3 className="text-lg font-medium text-ink mb-4">Rozkład ogłoszeń według typu</h3>
        <p className="text-ink-muted">Brak danych do wyświetlenia</p>
      </div>
    )
  }

  const sortedTypes = Object.entries(noticesByType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)

  const maxCount = Math.max(...sortedTypes.map(([, count]) => count))

  return (
    <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
      <h3 className="text-lg font-medium text-ink mb-4">Rozkład ogłoszeń według typu</h3>
      <div className="space-y-3">
        {sortedTypes.map(([type, count]) => (
          <div key={type} className="flex items-center">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink truncate" title={getNoticeTypeLabel(type)}>{getNoticeTypeLabel(type).replace(/\s*\(.*\)$/, '') || 'Nieznany'}</p>
              <div className="w-full bg-surface-inset rounded-full h-2">
                <div
                  className="bg-brand h-2 rounded-full"
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
            </div>
            <div className="ml-4 text-sm font-semibold text-ink">{count}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const DashboardPage = () => {
  const { getStats, loading, error } = useApi()
  const navigate = useNavigate()
  const [stats, setStats] = useState<StatsData | null>(null)

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getStats()
        setStats(data)
      } catch (err) {
        console.error('Failed to load stats:', err)
      }
    }
    loadStats()
  }, [getStats])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
        <p className="text-danger">Błąd podczas ładowania statystyk: {error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-ink">Dashboard</h2>
        <button
          onClick={() => navigate('/tenders')}
          className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md font-medium"
        >
          Przeglądaj ogłoszenia
        </button>
      </div>

      {stats && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-section">
            <StatCard
              title="Łączna liczba ogłoszeń"
              value={stats.total_notices?.toLocaleString('pl-PL') || '0'}
              icon={Database}
              color="blue"
              data-testid="total-notices"
            />
            <StatCard
              title="Rozmiar bazy danych"
              value={formatFileSize((stats.database_size_mb || 0) * 1024 * 1024)}
              icon={HardDrive}
              color="green"
            />
            <StatCard
              title="Liczba typów ogłoszeń"
              value={Object.keys(stats.notices_by_type || {}).length.toString()}
              icon={BarChart3}
              color="purple"
            />
            <StatCard
              title="Ostatnia synchronizacja"
              value={stats.last_sync ? formatDateTime(stats.last_sync) : 'Brak'}
              icon={Calendar}
              color="orange"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NoticeTypeChart noticesByType={stats.notices_by_type} />

            <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
              <h3 className="text-lg font-medium text-ink mb-4">Szybkie akcje</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/tenders')}
                  className="w-full text-left px-4 py-3 bg-surface hover:bg-surface-hover rounded-md transition-colors"
                >
                  <div className="font-medium text-ink">Przeglądaj wszystkie ogłoszenia</div>
                  <div className="text-sm text-ink-secondary">Zobacz pełną listę ogłoszeń</div>
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full text-left px-4 py-3 bg-surface hover:bg-surface-hover rounded-md transition-colors"
                >
                  <div className="font-medium text-ink">Odśwież statystyki</div>
                  <div className="text-sm text-ink-secondary">Załaduj najnowsze dane z bazy</div>
                </button>
              </div>
            </div>
          </div>

        </>
      )}

      {!stats && !loading && (
        <div className="bg-warning/5 border border-warning/20 rounded-md p-4">
          <p className="text-warning">
            Brak danych statystycznych. Upewnij się, że baza danych została zainicjalizowana.
          </p>
        </div>
      )}
    </div>
  )
}

export default DashboardPage
